import React, { useEffect, useRef, useState } from 'react';
import { Box, Grid2 } from '@mui/material';

import CustomTable from '@ui-components/Table/CustomTable';
import CustomTableBody from '@ui-components/Table/CustomTableBody';
import CustomTableRow from '@ui-components/Table/CustomTableRow';
import CustomTableHead from '@ui-components/Table/CustomTableHead';
import CustomTableCell from '@ui-components/Table/CustomTableCell';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomChip from '@ui-components/Chip/CustomChip';

import FilterIcon from '@assets/images/Filter.svg';
import SearchIcon from '@assets/images/search.svg';
import CustomButton from '@ui-components/Button/CustomButton';
import { useGetPartnersList } from '../../API/hooks/PartnerHook';
import { rowsPerPage } from '../constants';

import { useLocation, useNavigate } from 'react-router-dom';
import CustomPagination from '@ui-components/Table/CustomPagination';
import CustomTextField from '@ui-components/TextField/CustomTextField';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import useDebouncing from '../components/useDebouncing';
import { getEndItem, getStartItem, getSortIcon } from '../components/Sorting';
import {
  NEW_PARTNER_ROUTE_PATH,
  PARTNER_ROUTE_PATH,
  VIEW_PARTNER_ROUTE_PATH,
} from '../../routers/routePaths';
import EditOnlyIcon from '@assets/images/editOnly.svg';
import CustomSnackbar from '@ui-components/Snackbar/CustomSnackbar';
import FacilityList from '@assets/images/facilityList.svg';
import { useGetPartnerFacilityFilterData } from '../../API/hooks/PartnerFacilityFilterHook';
import CustomDivider from '@ui-components/Divider/CustomDivider';
import CustomPopover from '@ui-components/Popover/CustomPopover';
import { StatusFilterSelector } from '../components/StatusFilterSelector';
import { CustomBadge, CustomLink } from '@qler-view/shared_lib/src';
import { hasRole } from '../login/storageUtility';
import { useFilters } from '../../routers/FilterContext';

const PartnersList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const toast = query.get('toast');

  const [page, setPage] = useState(0);
  const userId = localStorage.getItem('userId') ?? '0';
  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortType, setSortType] = useState<'asc' | 'desc' | undefined>(
    undefined
  );

  const [openToast, setOpenToast] = useState(false);
  const [message, setMessage] = useState('');
  const { filterContext, setFilterContext, getFilterCounts } = useFilters();

  useEffect(() => {
    if (toast === 'success') {
      setMessage('Partner saved successfully!');
      setOpenToast(true);
    } else if (toast === 'update') {
      setMessage('Partner updated successfully!');
      setOpenToast(true);
    }

    navigate(PARTNER_ROUTE_PATH, { replace: true });
  }, [toast, navigate]);

  const handleClose_SnackBar = () => {
    setOpenToast(false);
  };

  const [searchTerm, setSearchTerm] = useState<string>(
    filterContext.searchedValue
  );
  const debouncedSearchTerm = useDebouncing(searchTerm, 500);
  const [selectedStatusId, setSelectedStatusId] = useState<number[]>(
    filterContext.statusIds
  );
  const [selectedFilterStatusId, setSelectedFilterStatusId] = useState<
    number[]
  >(filterContext.statusIds);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, selectedStatusId]);

  const { PartnersListResponse, isLoading } = useGetPartnersList({
    userId: userId,
    page: page,
    size: rowsPerPage,
    sortField,
    sortType,
    searchTerm: debouncedSearchTerm,
    statusId: selectedStatusId,
  });

  const PartnerListCount = PartnersListResponse?.totalRecords ?? 0;
  const [anchorFacilityListEl, setanchorFacilityListEl] =
    useState<HTMLElement | null>(null);
  const { PartnerFacilityFilterData, refetch: refetchFilters } =
    useGetPartnerFacilityFilterData();
  const handleFacilityListFilterClick = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    setanchorFacilityListEl(event.currentTarget);
    refetchFilters();
  };

  useEffect(() => {
    setSelectedStatusId(filterContext.statusIds);
    setSearchTerm(filterContext.searchedValue);
  }, [filterContext]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus(); // Focus the input field
    }
  }, [searchTerm]);

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    newPage: number
  ) => {
    setPage(newPage - 1);
  };

  const handleSort = (field: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortField === field && sortType === 'asc') {
      direction = 'desc';
    }
    setSortField(field);
    setSortType(direction);
  };

  const handlePartnerSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    // Remove leading spaces
    value = value.replace(/^\s+/, '');

    // Replace multiple spaces with a single space
    value = value.replaceAll(/\s{2,}/g, ' ');

    setSearchTerm(value);
    setFilterContext({
      ...filterContext,
      searchedValue: value,
    });
  };

  const handleEditPartnerInfo = (partnerId: number) => {
    navigate(NEW_PARTNER_ROUTE_PATH + `/${partnerId}`, {
      replace: true,
      state: { isList: true },
    });
  };

  const handlePartnerFacilityMapList = (partnerId: number) => {
    navigate(`/ops/facility/partner/${partnerId}`);
    setFilterContext({
      ...filterContext,
      searchedValue: '',
    });
  };

  const handleViewPartnerInfo = (partnerId: number) => {
    navigate(VIEW_PARTNER_ROUTE_PATH + `/${partnerId}`, { replace: true });
  };
  const handleStatusFilter = (statusIds: number[]) => {
    setSelectedFilterStatusId(statusIds);
  };
  const handleApplyFilters = () => {
    setSelectedStatusId(selectedFilterStatusId);
    setFilterContext({
      ...filterContext,
      statusIds: selectedFilterStatusId,
    });
  };
  return (
    <Box>
      {openToast && (
        <CustomSnackbar
          id="intake-form-tost"
          successOrError={'success'}
          open={openToast}
          handleClose={handleClose_SnackBar}
          toastrMessage={message}
        />
      )}
      <Box className="pt-20">
        <Grid2 container className="align-items-center">
          <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
            <CustomTypography
              id="page-title"
              variant="h4"
              className="text-align-left"
            >
              Partners
            </CustomTypography>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
            <Box className="flex float-right">
              <Box>
                <CustomTextField
                  id="txt-search-partner"
                  name="search-partner"
                  placeholder="Search Partner"
                  size="small"
                  type="text"
                  value={searchTerm}
                  onChange={handlePartnerSearch}
                  startAdornment={
                    <CustomIconButton
                      id="search-icon"
                      size="small"
                      icon={<SearchIcon />}
                    />
                  }
                />
              </Box>
              <Box className="ml-20">
                <CustomBadge
                  id={`notification`}
                  badgeContent={getFilterCounts(filterContext).totalCount}
                  color="error"
                  max={99}
                >
                  <CustomChip
                    aria-haspopup="true"
                    onClick={(e) => {
                      handleFacilityListFilterClick(e);
                    }}
                    className="box-border2"
                    id="demo-chip"
                    label="Filters"
                    variant="outlined"
                    icon={<FilterIcon />}
                    iconPosition="left"
                  />
                </CustomBadge>
              </Box>
              {hasRole(['ops-admin']) && (
                <Box className="ml-20">
                  <CustomButton
                    variant="contained"
                    id="btn-add-partner"
                    color="primary"
                    size="large"
                    onClick={() => {
                      navigate(NEW_PARTNER_ROUTE_PATH);
                    }}
                  >
                    Add Partner
                  </CustomButton>
                </Box>
              )}
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      <Box className="pt-20">
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CustomTypography
              id="loading-text"
              variant="h5"
              className="text-align-center pt-32"
              color="textDisabled"
            >
              Loading...
            </CustomTypography>
          </Box>
        ) : (
          <CustomTable id="table-partners">
            <CustomTableHead id="table-head-partner">
              <CustomTableRow id="partner-header">
                <CustomTableCell
                  id="table-cell-partner-id"
                  onClick={() => handleSort('partnerBusinessId')}
                >
                  PARTNER ID &nbsp;
                  {sortField === 'partnerBusinessId' && getSortIcon(sortType)}
                </CustomTableCell>
                <CustomTableCell
                  id="table-cell-partner-name"
                  onClick={() => handleSort('partnerName')}
                >
                  PARTNER NAME &nbsp;{' '}
                  {sortField === 'partnerName' && getSortIcon(sortType)}
                </CustomTableCell>
                <CustomTableCell
                  id="table-cell-partner-alias"
                  onClick={() => handleSort('alias')}
                >
                  ALIAS &nbsp; {sortField === 'alias' && getSortIcon(sortType)}
                </CustomTableCell>

                <CustomTableCell
                  id="table-cell-partner-website"
                  onClick={() => handleSort('website')}
                >
                  WEBSITE &nbsp;
                  {sortField === 'website' && getSortIcon(sortType)}
                </CustomTableCell>
                <CustomTableCell
                  id="table-cell-partner-type"
                  onClick={() => handleSort('partnerTypeName')}
                >
                  TYPE &nbsp;
                  {sortField === 'partnerTypeName' && getSortIcon(sortType)}
                </CustomTableCell>
                <CustomTableCell
                  id="table-cell-partner-status"
                  onClick={() => handleSort('status')}
                >
                  STATUS &nbsp;
                  {sortField === 'status' && getSortIcon(sortType)}
                </CustomTableCell>
                <CustomTableCell id="table-cell-partner-action">
                  ACTIONS &nbsp;
                </CustomTableCell>
              </CustomTableRow>
            </CustomTableHead>
            {PartnersListResponse &&
            PartnersListResponse?.partnerList.length > 0 ? (
              <CustomTableBody id="table-body-partner">
                {PartnersListResponse?.partnerList.map((partner) => (
                  <CustomTableRow
                    key={partner.id}
                    id={`table-row-partner-${partner.id}`}
                  >
                    <CustomTableCell id={`table-cell-partner-id-${partner.id}`}>
                      <CustomLink
                        id={`link-partner-id-${partner.id}`}
                        color="primary"
                        onClick={() => {
                          handleViewPartnerInfo(partner.id);
                        }}
                      >
                        {partner.partnerBusinessId}
                      </CustomLink>
                    </CustomTableCell>
                    <CustomTableCell
                      id={`table-cell-partner-name-${partner.id}`}
                    >
                      {partner.partnerName}
                    </CustomTableCell>
                    <CustomTableCell
                      id={`table-cell-partner-alias-${partner.id}`}
                    >
                      {partner.alias || 'N/A'}
                    </CustomTableCell>

                    <CustomTableCell
                      id={`table-cell-partner-website-${partner.id}`}
                    >
                      {partner.website || 'N/A'}
                    </CustomTableCell>
                    <CustomTableCell
                      id={`table-cell-partner-type-${partner.id}`}
                    >
                      {partner.partnerTypeName || 'N/A'}
                    </CustomTableCell>
                    <CustomTableCell
                      id={`table-cell-partner-status-${partner.id}`}
                    >
                      {partner.status}
                    </CustomTableCell>
                    <CustomTableCell
                      id={`table-cell-partner-action-${partner.id}`}
                      className="width-10"
                    >
                      <Box className="flex align-items-center">
                        <Box
                          className="flex align-items-center"
                          onClick={() => {
                            handlePartnerFacilityMapList(partner.id);
                          }}
                        >
                          <CustomIconButton
                            id="user-icon"
                            size="small"
                            icon={<FacilityList />}
                            onClick={() => {}}
                          />
                          <Box className="display-count cursor-pointer">
                            {partner.facilityCount}
                          </Box>
                        </Box>
                        {hasRole(['ops-admin']) && (
                          <Box className="ml-16">
                            <CustomIconButton
                              id="edit-icon"
                              size="small"
                              icon={<EditOnlyIcon />}
                              onClick={() => {
                                handleEditPartnerInfo(partner.id);
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </CustomTableCell>
                  </CustomTableRow>
                ))}
              </CustomTableBody>
            ) : (
              <CustomTableBody id="table-body-partner">
                <CustomTableRow id="table-row-partner">
                  <CustomTableCell id="table-cell-partner-id" colspan={8}>
                    <Box className="flex flex-justify-center align-items-center">
                      <CustomTypography
                        id="no-partners-found"
                        variant="body2"
                        color="textDisabled"
                      >
                        No Partners Found
                      </CustomTypography>
                    </Box>
                  </CustomTableCell>
                </CustomTableRow>
              </CustomTableBody>
            )}
          </CustomTable>
        )}
        {PartnersListResponse && PartnerListCount > 0 && (
          <Box className="mt-32">
            <Grid2 container className="align-items-center">
              <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                <Box className="flex justify-content-start">
                  <CustomTypography
                    id="showing-row-count-text"
                    variant="body2"
                    color="textDisabled"
                  >
                    Showing {getStartItem(page, rowsPerPage)} -{' '}
                    {getEndItem(page, rowsPerPage, PartnerListCount)} of{' '}
                    {PartnerListCount} partners
                  </CustomTypography>
                </Box>
              </Grid2>
              {PartnerListCount > rowsPerPage && (
                <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
                  <Box display="flex" justifyContent="end">
                    <CustomPagination
                      id="partner-list-pagination"
                      count={Math.ceil(PartnerListCount / rowsPerPage)}
                      page={page + 1}
                      onPageChange={handlePageChange}
                      color="standard"
                    />
                  </Box>
                </Grid2>
              )}
            </Grid2>
          </Box>
        )}
      </Box>
      <Box>
        <CustomPopover
          open={Boolean(anchorFacilityListEl)}
          anchorEl={anchorFacilityListEl}
          onClose={() => setanchorFacilityListEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          className="mt-4"
        >
          <Box className="p-24">
            <Box className="text-align-left">
              <CustomTypography
                id="intake-form-filter-title"
                variant="h4"
                color="textPrimary"
              >
                Filters
              </CustomTypography>
              <Box className="pt-8">
                <CustomTypography
                  id="intake-form-filter-subtitle"
                  variant="body2"
                  color="textDisabled"
                >
                  Filter records by status
                </CustomTypography>
              </Box>
            </Box>
            <Box className="pt-16">
              <CustomDivider />
            </Box>
            <Box className="flex flex-column pt-16">
              <Grid2 container justifyContent="center" spacing={2}>
                <Grid2 size={{ xs: 12 }}>
                  <Box>
                    <Box className="flex-between-center">
                      <Box>
                        <CustomTypography
                          id="facility-list-status-filter-title"
                          variant="body2"
                          color="textDisabled"
                        >
                          STATUS
                        </CustomTypography>
                      </Box>
                    </Box>
                    <Box className="pt-8 pb-8">
                      <StatusFilterSelector
                        data={PartnerFacilityFilterData}
                        value={selectedFilterStatusId}
                        isForConsult={false}
                        onChange={handleStatusFilter}
                      />
                    </Box>
                  </Box>
                </Grid2>
              </Grid2>
            </Box>
            <Box className="pt-16">
              <Grid2 container>
                <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                  <Box className="display-flex justify-end">
                    <Box className="pr-12">
                      <CustomButton
                        id="cancel-facility-user-button"
                        onClick={() => {
                          setanchorFacilityListEl(null);
                          setSelectedFilterStatusId([]);
                          setSelectedStatusId([]);
                          setFilterContext({
                            ...filterContext,
                            statusIds: [],
                          });
                        }}
                        variant="outlined"
                      >
                        Clear
                      </CustomButton>
                    </Box>
                    <Box>
                      <CustomButton
                        id="apply-facility-user-filter-button"
                        onClick={() => {
                          setanchorFacilityListEl(null);
                          handleApplyFilters();
                        }}
                        variant="contained"
                      >
                        Apply
                      </CustomButton>
                    </Box>
                  </Box>
                </Grid2>
              </Grid2>
            </Box>
          </Box>
        </CustomPopover>
      </Box>
    </Box>
  );
};

export default PartnersList;
